/**
 * SettingsWatiCard — composer for the WATI Integration card on the
 * Settings page. Originally 381 lines (Iter 138, Feb 2026); split in
 * Iter 156.9 (Feb 28, 2026) into:
 *
 *   • `WatiChannelHealth.jsx`  — connection indicator + per-country health
 *   • `WatiAdminActions.jsx`   — the 6-button operational cluster
 *   • (this file)              — webhook URLs block + composition + the
 *                                six admin sibling cards
 *
 * All state and handlers continue to live in SettingsPage and are passed
 * through (same explicit-props pattern as iters 135/137/138). Same
 * default export so SettingsPage doesn't need to change.
 */
import { MessageSquare } from "lucide-react";

import DuplicateLeadsCard from "../DuplicateLeadsCard";
import MisroutedCsAuditCard from "../MisroutedCsAuditCard";
import BroadcastDedupCard from "../BroadcastDedupCard";
import TelecmiChubCard from "../TelecmiChubCard";
import SlackAlertsCard from "../SlackAlertsCard";
import MockCallStarterCard from "../MockCallStarterCard";

import WatiChannelHealth from "./WatiChannelHealth";
import WatiAdminActions from "./WatiAdminActions";

const WatiWebhookUrls = ({ watiStatus }) => (
  <div className="space-y-4">
    {watiStatus?.is_preview && (
      <div className="p-3 bg-[#FEE2E2] border border-red-200 rounded-md text-xs text-red-800" data-testid="preview-warning">
        <strong>Preview environment.</strong> WATI auto-sync is disabled here so this env does not consume your live WATI rate limit.
        Chats you see here are from the preview database. Use your <strong>live/production URL</strong> to see real incoming chats.
      </div>
    )}
    <h3 className="font-medium text-[#1C1917]">Webhook Setup (Real-time Sync)</h3>
    <p className="text-sm text-[#57534E]">
      Paste each URL below into its matching WATI dashboard. India → WATI India, US → WATI US.
    </p>

    <div className="p-3 bg-[#E0F2F1] rounded-md">
      <p className="text-xs text-teal-800 font-medium mb-1">India Webhook URL:</p>
      <code className="text-xs text-teal-900 break-all block" data-testid="webhook-url-india">
        {process.env.REACT_APP_BACKEND_URL}/api/wati/webhook
      </code>
    </div>

    <div className="p-3 bg-[#E0E7FF] rounded-md">
      <p className="text-xs text-indigo-800 font-medium mb-1">US Webhook URL:</p>
      <code className="text-xs text-indigo-900 break-all block" data-testid="webhook-url-us">
        {process.env.REACT_APP_BACKEND_URL}/api/wati-us/webhook
      </code>
    </div>

    <div className="p-3 bg-[#FEF3C7] rounded-md">
      <p className="text-xs text-amber-800 font-medium mb-2">Setup Instructions (repeat for each WATI account):</p>
      <ol className="text-xs text-amber-700 space-y-1 list-decimal list-inside">
        <li>Login to your WATI dashboard</li>
        <li>Go to Settings → Webhooks → Add Webhook</li>
        <li>Paste the matching URL above (India URL into India WATI, US URL into US WATI)</li>
        <li>Select events: &quot;Message Received&quot; (and &quot;Message Sent&quot; if you want broadcast/outgoing sync)</li>
        <li>Save and enable the webhook</li>
      </ol>
    </div>
  </div>
);

export default function SettingsWatiCard({
  user,
  watiStatus,
  // sync
  syncing, handleSyncWati, syncingMessages, handleSyncMessages,
  // dedupe
  dupeCheckResult, mergingDupes, handleCheckDuplicates, handleMergeDuplicates,
  // re-route / reclassify / repair
  reroutingAll, handleRerouteAll, rerouteResult,
  reclassifyingBc, handleReclassifyBroadcasts, reclassifyResult,
  repairingMedia, handleRepairMedia, mediaRepairResult,
  handleRefreshAllWatiStatuses, refreshingStatuses, statusRefreshProgress,
}) {
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  return (
    <>
      {isAdmin && (
        <div className="bento-card lg:col-span-2">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-[#15803D]/10 rounded-md">
              <MessageSquare size={20} className="text-[#15803D]" />
            </div>
            <h2 className="text-xl font-medium text-[#1C1917]" style={{ fontFamily: "Playfair Display, serif" }}>
              WATI WhatsApp Integration
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <WatiWebhookUrls watiStatus={watiStatus} />

            <div className="space-y-4">
              <h3 className="font-medium text-[#1C1917]">Auto-Sync & Status</h3>
              <p className="text-sm text-[#57534E]">
                Contacts are automatically synced every 5 minutes.
              </p>
              <WatiChannelHealth watiStatus={watiStatus} />
              <WatiAdminActions
                watiStatus={watiStatus}
                syncing={syncing} handleSyncWati={handleSyncWati}
                syncingMessages={syncingMessages} handleSyncMessages={handleSyncMessages}
                handleRefreshAllWatiStatuses={handleRefreshAllWatiStatuses}
                refreshingStatuses={refreshingStatuses}
                statusRefreshProgress={statusRefreshProgress}
                dupeCheckResult={dupeCheckResult}
                mergingDupes={mergingDupes}
                handleCheckDuplicates={handleCheckDuplicates}
                handleMergeDuplicates={handleMergeDuplicates}
                reroutingAll={reroutingAll}
                handleRerouteAll={handleRerouteAll}
                rerouteResult={rerouteResult}
                reclassifyingBc={reclassifyingBc}
                handleReclassifyBroadcasts={handleReclassifyBroadcasts}
                reclassifyResult={reclassifyResult}
                repairingMedia={repairingMedia}
                handleRepairMedia={handleRepairMedia}
                mediaRepairResult={mediaRepairResult}
              />
              <p className="text-xs text-[#A8A29E] text-center">
                Sync messages to import WhatsApp conversation history
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Sibling admin cards — admin/super_admin only unless noted */}
      {isAdmin && <DuplicateLeadsCard />}
      {isAdmin && <MisroutedCsAuditCard />}
      {/* Iter 110 companion: cleanup duplicate broadcasts */}
      {isAdmin && <BroadcastDedupCard />}
      {/* TeleCMI CHUB — visible to every user so each agent can save their own
          CHUB credentials. Admins also see a Team panel listing which agents
          are configured. */}
      <TelecmiChubCard />
      {/* Missed-call Slack alerts — fires a rich Slack message when an inbound
          call rings-out unanswered. */}
      {isAdmin && <SlackAlertsCard />}
      {isAdmin && <MockCallStarterCard />}
    </>
  );
}
